import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient'; 
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ChatbotWidget from '../../components/ui/ChatbotWidget';
import ProfileHeader from './components/ProfileHeader';
import AcademicInfoTab from './components/AcademicInfoTab';
import SavedInstitutionsTab from './components/SavedInstitutionsTab';
import RecommendationsTab from './components/RecommendationsTab';
import PrivacySettingsTab from './components/PrivacySettingsTab';
import Icon from '../../components/AppIcon';
import EditProfileModal from './components/EditProfileModal';
import OnboardingModal from './components/OnboardingModal';

const StudentProfile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('academic');
  
  const [user, setUser] = useState(null); 
  const [profileRaw, setProfileRaw] = useState(null); 
  
  const [student, setStudent] = useState({});
  const [academicInfo, setAcademicInfo] = useState({});
  const [savedInstitutions, setSavedInstitutions] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [privacySettings, setPrivacySettings] = useState({});
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [loading, setLoading] = useState(true);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // --- LOGIQUE DE MAPPING INTELLIGENT ---
  const mapJobToInterest = (job) => {
    if (!job) return null;
    const lowerJob = job.toLowerCase();
    if (lowerJob.includes('ingé') || lowerJob.includes('tech') || lowerJob.includes('dev')) return 'Ingénierie';
    if (lowerJob.includes('médecin') || lowerJob.includes('santé') || lowerJob.includes('pharma')) return 'Médecine';
    if (lowerJob.includes('commerce') || lowerJob.includes('gestion') || lowerJob.includes('business')) return 'Commerce';
    if (lowerJob.includes('archi')) return 'Architecture';
    if (lowerJob.includes('droit') || lowerJob.includes('avocat')) return 'Droit';
    return null; 
  };

  const fetchProfileData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/login'); return; }
      setUser(user);

      let { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) profile = {}; 
      setProfileRaw(profile);

      if (profile && (!profile.grade_level || !profile.study_field || !profile.city)) {
          setShowOnboarding(true);
      } else {
          setShowOnboarding(false);
      }

      // Construction Student
      const hasName = profile?.first_name && profile?.last_name;
      setStudent({
        id: user.id,
        name: hasName ? `${profile.first_name} ${profile.last_name}` : user.email.split('@')[0],
        email: user.email,
        initials: hasName ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase() : "ST",
        city: profile?.city || "Non renseigné",
        region: "Maroc",
        joinDate: new Date(user.created_at).toLocaleDateString(),
        avatar: profile?.avatar_url || null, // MODIFIÉ : On lit l'avatar ici !
      });

      // --- PRÉPARATION DES DONNÉES ACADÉMIQUES ---
      let interests = profile?.career_interests || [];
      let regions = profile?.preferred_regions || [];
      let subjects = profile?.preferred_subjects || [];

      if (interests.length === 0 && profile?.dream_job) {
          const mappedInterest = mapJobToInterest(profile.dream_job);
          if (mappedInterest) interests = [mappedInterest];
      }
      
      if (regions.length === 0 && profile?.city) {
          regions = [profile.city];
      }

      setAcademicInfo({
        baccalaureatTrack: profile?.study_field || profile?.target_track || "Non défini",
        currentSchool: profile?.current_school || "À compléter",
        gradeLevel: profile?.grade_level,
        baccalaureatYear: profile?.bac_year || "2025",
        
        regional_grade: profile?.regional_grade,
        continuous_control_grade: profile?.continuous_control_grade,
        national_grade: profile?.national_grade,
        overallGrade: calcOverallGrade(profile),
        biof: profile?.biof || false,

        preferredSubjects: subjects,
        careerInterests: interests,
        preferredRegions: regions
      });
      
      setSavedInstitutions([]);
      setRecommendations([]);
      setPrivacySettings({ shareProfileWithInstitutions: true, language: "fr" });
      
      // --- MODIFIÉ : CALCUL DU POURCENTAGE RÉEL ---
      let score = 0;
      // Critères (Total = 100 points)
      const criteria = [
          { check: profile?.first_name && profile?.last_name, weight: 20 }, // Nom
          { check: profile?.city, weight: 10 },                             // Ville
          { check: profile?.avatar_url, weight: 10 },                       // Photo
          { check: profile?.grade_level, weight: 10 },                      // Niveau
          { check: profile?.study_field, weight: 20 },                      // Filière
          { check: profile?.current_school, weight: 10 },                   // École
          { check: profile?.bac_year, weight: 10 },                         // Année bac
          { check: (profile?.dream_job || (profile?.career_interests && profile.career_interests.length > 0)), weight: 10 } // Intérêts
      ];

      criteria.forEach(c => {
          if (c.check) score += c.weight;
      });
      setCompletionPercentage(Math.min(100, score));
      // ---------------------------------------------

    } catch (error) {
      console.error("Erreur chargement:", error);
    } finally {
      setLoading(false);
    }
  };

  const calcOverallGrade = (p) => {
    if (p?.regional_grade && p?.continuous_control_grade && p?.national_grade) {
        return ((p.regional_grade * 0.25) + (p.continuous_control_grade * 0.25) + (p.national_grade * 0.50)).toFixed(2);
    }
    return null;
  };

  useEffect(() => {
    fetchProfileData();
  }, [navigate]);

  // --- SAUVEGARDE EN BASE ---
  const handleUpdateAcademicInfo = async (updatedInfo) => {
    setAcademicInfo(updatedInfo); 

    try {
        const updates = {
            grade_level: updatedInfo.gradeLevel,
            study_field: updatedInfo.baccalaureatTrack,
            target_track: updatedInfo.baccalaureatTrack,
            current_school: updatedInfo.currentSchool,
            bac_year: updatedInfo.baccalaureatYear,
            
            regional_grade: updatedInfo.regional_grade,
            continuous_control_grade: updatedInfo.continuous_control_grade,
            national_grade: updatedInfo.national_grade,
            biof: updatedInfo.biof,

            preferred_subjects: updatedInfo.preferredSubjects,
            career_interests: updatedInfo.careerInterests,
            preferred_regions: updatedInfo.preferredRegions,
            
            updated_at: new Date()
        };

        const { error } = await supabase
            .from('profiles')
            .update(updates)
            .eq('id', user.id);

        if (error) throw error;
        console.log("Sauvegarde réussie !");
        
        // On recharge pour recalculer le % si on a ajouté des infos manquantes
        fetchProfileData();

    } catch (error) {
        console.error("Erreur sauvegarde :", error);
        alert("Erreur de sauvegarde.");
    }
  };

  // --- RENDU ---
  const tabs = [
    { id: 'academic', label: 'Informations Académiques', icon: 'GraduationCap', description: 'Profil scolaire et préférences' },
    { id: 'saved', label: 'Établissements Sauvegardés', icon: 'Bookmark', description: 'Institutions et candidatures' },
    { id: 'recommendations', label: 'Recommandations', icon: 'Sparkles', description: 'Suggestions personnalisées' },
    { id: 'privacy', label: 'Confidentialité', icon: 'Shield', description: 'Paramètres et données' }
  ];

  const renderTabContent = () => {
     switch (activeTab) {
      case 'academic': return <AcademicInfoTab academicInfo={academicInfo} onUpdate={handleUpdateAcademicInfo} />;
      case 'saved': return <SavedInstitutionsTab savedInstitutions={savedInstitutions} onRemove={()=>{}} onUpdateStatus={()=>{}} />;
      case 'recommendations': return <RecommendationsTab recommendations={recommendations} onRefresh={()=>{}} onSaveInstitution={()=>{}} />;
      case 'privacy': return <PrivacySettingsTab privacySettings={privacySettings} onUpdate={()=>{}} />;
      default: return null;
    }
  };

  if (loading) return <div className="flex h-screen items-center justify-center">Chargement...</div>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <BreadcrumbNavigation />
        
        {completionPercentage < 50 && !showOnboarding && (
          <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-center justify-between animate-fade-in">
            <div className="flex items-center gap-3 text-orange-800">
              <Icon name="AlertCircle" />
              <div>
                <p className="font-bold">Votre profil peut être amélioré</p>
                <p className="text-sm">Il manque quelques infos pour personnaliser vos résultats.</p>
              </div>
            </div>
            <button onClick={() => setShowOnboarding(true)} className="px-4 py-2 bg-white border border-orange-200 text-orange-700 font-medium rounded-lg hover:bg-orange-100 transition-smooth">
              Compléter mon parcours
            </button>
          </div>
        )}

        <div className="relative">
            <div className="absolute top-4 right-4 z-10">
                <button onClick={() => setIsEditModalOpen(true)} className="flex items-center gap-2 px-3 py-1.5 bg-background/80 backdrop-blur border border-border rounded-lg text-sm font-medium hover:bg-muted transition-smooth shadow-sm">
                    <Icon name="Edit" size={14} /> Modifier
                </button>
            </div>
            {/* MODIFIÉ : On passe la fonction de rafraichissement pour l'avatar */}
            <ProfileHeader 
                student={student} 
                completionPercentage={completionPercentage} 
                onAvatarUpdate={fetchProfileData}
            />
        </div>

        <div className="bg-card border border-border rounded-lg mb-6 mt-6">
          <div className="border-b border-border">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-smooth border-b-2 ${activeTab === tab.id ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                  <Icon name={tab.icon} size={16} /> <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="min-h-[600px]">{renderTabContent()}</div>
      </main>
      
      <ChatbotWidget />
      {isEditModalOpen && <EditProfileModal user={user} currentProfile={profileRaw} onClose={() => setIsEditModalOpen(false)} onUpdate={fetchProfileData} />}
      {showOnboarding && <OnboardingModal user={user} onComplete={() => { setShowOnboarding(false); fetchProfileData(); }} />}
    </div>
  );
};

export default StudentProfile;